type GenericPosition<T> = {
	readonly x: T;
	readonly y: T;
};

const originPosition: GenericPosition<number> = { x: 0, y: 0 };

type PixelPosition = GenericPosition<number>;

function addPositions(
	position1: GenericPosition<number>,
	position2: GenericPosition<number>,
): GenericPosition<number> {
	return { x: position1.x + position2.x, y: position1.y + position2.y };
}

function roundPosition(
	position: GenericPosition<number>,
): GenericPosition<number> {
	return { x: Math.round(position.x), y: Math.round(position.y) };
}

function subtractPositions(
	position1: GenericPosition<number>,
	position2: GenericPosition<number>,
): GenericPosition<number> {
	return { x: position1.x - position2.x, y: position1.y - position2.y };
}

function truncateDimension(d: number, limit: number): number {
	if (d > limit) {
		return limit;
	}
	if (d < -limit) {
		// Prevent putting -0 into the system
		if (limit === 0) {
			return 0;
		}
		return -limit;
	}
	return d;
}

function truncatePosition(
	p: GenericPosition<number>,
	limitX: number,
	limitY: number,
): GenericPosition<number> {
	return {
		x: truncateDimension(p.x, limitX),
		y: truncateDimension(p.y, limitY),
	};
}

function multiplyPosition(
	p: GenericPosition<number>,
	scale: number,
): GenericPosition<number> {
	return { x: p.x * scale, y: p.y * scale };
}

function arePositionsClose(
	p1: GenericPosition<number>,
	p2: GenericPosition<number>,
	threshold: number,
): boolean {
	return Math.abs(p1.x - p2.x) < threshold && Math.abs(p1.y - p2.y) < threshold;
}

type GenericSize<T> = {
	readonly width: T;
	readonly height: T;
};

function multiplySize(
	{ width, height }: GenericSize<number>,
	multiplier: number,
): GenericSize<number> {
	return { width: width * multiplier, height: height * multiplier };
}

type Area = {
	readonly m2: number;
};

function areaFromMetres2(m2: number): Area {
	return { m2 };
}

function crossProduct(
	p1: GenericPosition<number>,
	p2: GenericPosition<number>,
	p3: GenericPosition<number>,
): number {
	return (p2.x - p1.x) * (p3.y - p1.y) - (p2.y - p1.y) * (p3.x - p1.x);
}

/**
 * Computes the convex hull of a set of 2D points using the Monotone Chain algorithm.
 * @param points An array of points.
 * @returns An array of points representing the convex hull, ordered counter-clockwise.
 */
function convexHull(
	points: GenericPosition<number>[],
): GenericPosition<number>[] {
	// 1. Handle edge cases: A convex hull requires at least 3 points.
	if (points.length < 3) {
		return Array.from(points);
	}

	// 2. Sort points lexicographically (first by x, then by y).
	// This is a crucial step for the algorithm to work.
	const sortedPoints = Array.from(points).sort((a, b) => {
		if (a.x === b.x) {
			return a.y - b.y;
		}
		return a.x - b.x;
	});

	// 3. Build the lower hull
	const lowerHull: GenericPosition<number>[] = [];
	for (const point of sortedPoints) {
		// While the last two points on the hull and the current point make a clockwise turn...
		while (
			lowerHull.length >= 2 &&
			crossProduct(
				lowerHull[lowerHull.length - 2],
				lowerHull[lowerHull.length - 1],
				point,
			) <= 0
		) {
			// ...remove the last point from the hull (it's not part of the convex set).
			lowerHull.pop();
		}
		lowerHull.push(point);
	}

	// 4. Build the upper hull
	// Iterate in reverse to build the other side of the hull.
	const upperHull: GenericPosition<number>[] = [];
	for (let i = sortedPoints.length - 1; i >= 0; i--) {
		const point = sortedPoints[i];
		// While the last two points on the hull and the current point make a clockwise turn...
		while (
			upperHull.length >= 2 &&
			crossProduct(
				upperHull[upperHull.length - 2],
				upperHull[upperHull.length - 1],
				point,
			) <= 0
		) {
			// ...remove the last point.
			upperHull.pop();
		}
		upperHull.push(point);
	}

	// 5. Combine the hulls
	// The first and last points of each hull are duplicates, so we remove them before concatenating.
	lowerHull.pop();
	upperHull.pop();

	return [...lowerHull, ...upperHull];
}

export type { Area, GenericSize, PixelPosition, GenericPosition };
export {
	convexHull,
	arePositionsClose,
	multiplyPosition,
	multiplySize,
	roundPosition,
	subtractPositions,
	areaFromMetres2,
	truncatePosition,
	addPositions,
	originPosition,
};
